import React, { useState } from "react";
import { bool, func, number, object, string } from "prop-types";
import { connect } from "react-redux";
import get from "lodash/get";
import { parseUrl } from "query-string";
import { Link } from "@quintype/components";

import TextField from "../../atoms/TextField";
import PasswordField from "../../atoms/PasswordField";
import Button from "../../atoms/Button";
import MessageView from "../../atoms/MessageView";
import SocialLogin from "../../molecules/SocialLogin";
import Icons from "../../icons";

import { getContinueToText } from "../../utils/utils";
import { login, oauthAuthorize } from "@quintype/bridgekeeper-js";

import "./login.m.css";

const Login = ({ googleClientId, facebookId, currentPath, sketchesHost, authHost, clientId, ssoLoginIsEnable }) => {
  const [user, setUser] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isLoaderActive, setIsLoaderActive] = useState(false);
  const params = parseUrl(currentPath);
  const redirectUrl = get(params, ["query", "redirect_uri"], sketchesHost);
  const callbackUrl = get(params, ["query", "callback_uri"], sketchesHost);
  const targetText = getContinueToText(callbackUrl);

  const setData = (e) => {
    const userObj = { ...user };
    const fieldName = e.target.name;
    userObj[fieldName] = e.target.value;

    // If error has been set then remove it
    if (e.target.value && userObj[`${fieldName}Error`]) {
      userObj[`${fieldName}Error`] = "";
    }

    // If error is set remove it since data is changed
    if (error) {
      setError(null);
    }

    // If success message is set remove it since data is changed
    if (successMessage) {
      setSuccessMessage(null);
    }

    setUser(userObj);
  };

  const loginHandler = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsLoaderActive(true);
    const userObj = {
      username: user.email.trim(),
      email: user.email.trim(),
      password: user.password,
    };
    if (user.email.length < 1 || user.password.length < 1) {
      setError({ message: "Please provide username and password" });
      setIsLoaderActive(false);
      return null;
    }
    login(userObj)
      .then(async ({ user, message }) => {
        if (!user) {
          setError({ message });
          setIsLoaderActive(false);
          return;
        }
        if (user["verification-status"]) {
          // User email is verified
          const allowAjax = true;
          const oauthResponse =
            ssoLoginIsEnable && (await oauthAuthorize(clientId, redirectUrl, callbackUrl, allowAjax));
          if (oauthResponse.redirect_uri) window.location.href = oauthResponse.redirect_uri;
        } else {
          // User needs to validate the email account so send out an email to verify
          const withoutVerifyCallBackUrl =
            authHost +
            "/api/auth/v1/oauth/authorize?client_id=" +
            clientId +
            "&response_type=code&redirect_uri=" +
            redirectUrl +
            "&callback_uri=" +
            callbackUrl;
          window.location.href = withoutVerifyCallBackUrl;
        }
      })
      .catch((_err) => {
        setIsLoaderActive(false);
      });
  };

  return (
    <form onSubmit={loginHandler}>
      <div styleName="container">
        <div styleName="wrapper">
          <a aria-label="Back Icon" styleName="back-button" href={callbackUrl}>
            <Icons height="14px" width="14px" type="back-icon" alt="Back Icon" />
            <span styleName="back-text">Back</span>
          </a>
          <div styleName="content">
            <Icons type="palo-bangla" alt="Prothom Alo" className="palo-logo" />
            <h3 styleName="login-text">Login</h3>
            <div styleName="continue-to">
              <span styleName="continue-to-text">to continue to</span>
              <span styleName="target-site">{targetText}</span>
            </div>
            <SocialLogin
              redirectUrl={redirectUrl}
              callbackUrl={callbackUrl}
              clientId={clientId}
              ssoLoginIsEnable={ssoLoginIsEnable}
              authHost={authHost}
              facebookId={facebookId}
              googleClientId={googleClientId}
            />
            <span styleName="or-text">Or</span>
            <TextField
              value={user.email}
              error={user.emailError}
              placeholder="Email"
              inputType="type2"
              name="email"
              onChange={setData}
            />
            <PasswordField
              value={user.password}
              error={user.passwordError}
              placeholder="Password"
              name="password"
              onChange={setData}
            />
            {error && (
              <MessageView
                type="error"
                text="Your email address or password is incorrect. Please check and try again"
              />
            )}
            <Button
              content="LOGIN"
              type="submit"
              buttonType="type3"
              isLoaderActive={isLoaderActive}
              selectedItem="login"
              buttonId="login"
            />
            <Link
              aria-label="Forgot password"
              href={`forgot-password?redirect-url=${redirectUrl}&callback-url=${callbackUrl}`}
              styleName="forgot-password"
            >
              Forgot password
            </Link>
            <span styleName="new-user">
              New to Prothom Alo?
              <Link
                aria-label="New to prothomalo"
                href={`authenticate?redirect-url=${redirectUrl}&callback-url=${callbackUrl}&auth-page=${authHost}/sign-up-page`}
              >
                Create an account
              </Link>
            </span>
          </div>
        </div>
        <div styleName="terms-privacy">
          By proceeding, you agree to{" "}
          <a aria-label="Terms of Use" styleName="terms-privacy-link" href={`${sketchesHost}/terms`}>
            Terms of Use
          </a>{" "}
          and{" "}
          <a aria-label="Privacy Policy" styleName="terms-privacy-link" href={`${sketchesHost}/privacy-policy`}>
            Privacy Policy
          </a>{" "}
          of Prothom Alo
        </div>
      </div>
    </form>
  );
};

Login.propTypes = {
  member: object,
  navigateToPage: func,
  checkForMemberUpdated: func,
  googleClientId: string,
  facebookId: string,
  setUser: func,
  currentPath: string,
  sketchesHost: string,
  authHost: string,
  clientId: number,
  ssoLoginIsEnable: bool,
};

const mapStateToProps = (state) => ({
  googleClientId: get(state, ["qt", "config", "publisher-attributes", "socialAuth", "google"], ""),
  facebookId: get(state, ["qt", "config", "publisher-attributes", "socialAuth", "fb"], ""),
  member: state.member || null,
  sketchesHost: get(state, ["qt", "config", "sketches-host"], "https://www.prothomalo.com"),
  authHost: get(state, ["qt", "config", "publisher-attributes", "auth_host"], null),
  clientId: get(state, ["qt", "config", "publisher-attributes", "sso_login", "client_id"], 0),
  ssoLoginIsEnable: get(state, ["qt", "config", "publisher-attributes", "sso_login", "is_enable"], true),
});

const mapDispatchToProps = (dispatch) => ({
  navigateToPage: (url) => global.app.navigateToPage(dispatch, url),
});

export default connect(mapStateToProps, mapDispatchToProps)(Login);
